"use client";

import { RiCalendarLine, RiGovernmentLine, RiMapPinLine, RiMusic2Line } from "@remixicon/react";
import { formatFestivalDatesWithPreferences } from "@workspace/shared-utils";
import { Card, CardContent } from "@workspace/ui/components/card";
import Link from "next/link";
import { memo } from "react";
import { Facepile } from "@/components/shared/facepile";
import { FacepileContainer } from "@/components/shared/facepile-skeleton";
import { FestivalPoster } from "@/components/shared/photos/festival-poster";
import { useUserPreferences } from "@/hooks/shared/use-user-preferences";
import type { FollowerInteraction } from "@/lib/data/shared/facepile-cached";
import { cn } from "@/lib/utils";

// Festival interface for artist pages - focused on artist-specific data
interface ArtistFestival {
	id: string;
	name: string;
	slug: string;
	location: string;
	venue_obj?: {
		id: string;
		name: string;
		slug: string;
		city: string;
		state?: string | null;
		country: string;
		venue_type?: string | null;
		capacity?: number | null;
	} | null;
	start_date: string;
	end_date: string;
	poster_url: string | null;
	description: string | null;
	artist_sets_count?: number;
	artist_performance_dates?: string[];
	schedule_status?: "lineup_only" | "complete";
	schedule_start_hour?: number | null;
	schedule_end_hour?: number | null;
	schedule_overnight?: boolean | null;
	schedule_day_start_hour?: number | null;
}

interface ArtistFestivalCardProps {
	festival: ArtistFestival;
	artistId: string;
	className?: string;
	priority?: boolean;
	attendees?: FollowerInteraction[];
	isFacepileLoading?: boolean;
	setInfo?: {
		id: string;
		start_time: string | null;
		end_time: string | null;
		alias_name: string | null;
		artist: {
			id: string;
			name: string;
			image_url: string | null;
		};
	};
}

/**
 * Festival card component for artist pages
 * Shows festivals where an artist has performed or will perform
 * Focuses on artist-specific data rather than user profile data
 */
export const ArtistFestivalCard = memo<ArtistFestivalCardProps>(
	({
		festival,
		className,
		priority = false,
		attendees = [],
		isFacepileLoading = false,
		setInfo,
	}) => {
		// Get user preferences for date formatting
		const userPreferences = useUserPreferences();

		// Navigate to festival detail page
		const linkHref = `/festivals/${festival.slug}`;

		return (
			<Card
				className={cn(
					"overflow-hidden",
					"hover:bg-muted/30 dark:hover:bg-muted/10",
					"active:bg-muted/50 dark:active:bg-muted/20",
					"active:scale-[0.99]",
					"transition-all duration-150",
					className,
				)}
			>
				<Link href={linkHref} className="block">
					<CardContent className="flex flex-col p-3">
						<div className="flex gap-3 flex-1">
							<FestivalPoster
								name={festival.name}
								posterUrl={festival.poster_url || null}
								size="sm"
								rounded="lg"
								priority={priority}
								className="flex-shrink-0"
							/>

							<div className="min-w-0 flex-1 flex flex-col justify-between">
								<div>
									<h3 className="font-semibold leading-tight">
										{festival.name}
									</h3>
									<div className="space-y-0.5 text-muted-foreground text-xs">
										{/* Location (City) */}
										<div className="flex items-center gap-1">
											<RiMapPinLine className="h-3 w-3 flex-shrink-0" />
											<span className="text-left">{festival.location}</span>
										</div>
										{/* Venue (if available) */}
										{festival.venue_obj && (
											<div className="flex items-center gap-1">
												<RiGovernmentLine className="h-3 w-3 flex-shrink-0" />
												<span className="text-left">{festival.venue_obj.name}</span>
											</div>
										)}
										{/* Dates */}
										<div className="flex items-center gap-1">
											<RiCalendarLine className="h-3 w-3 flex-shrink-0" />
											<span className="text-left">
												{formatFestivalDatesWithPreferences(
													festival.start_date,
													festival.end_date || festival.start_date,
													userPreferences,
												)}
											</span>
										</div>
										{/* Sets count row with facepile */}
										<div className="flex items-center justify-between gap-2">
											<div className="flex items-center gap-1">
												<RiMusic2Line className="h-3 w-3 flex-shrink-0" />
												<span className="text-left">
													{festival.artist_sets_count !== undefined && festival.artist_sets_count > 0
														? `${festival.artist_sets_count} set${festival.artist_sets_count !== 1 ? "s" : ""}`
														: "Sets: TBA"}
												</span>
											</div>

											{/* Facepile in same row */}
											<FacepileContainer hasData={attendees.length > 0}>
												<Facepile
													interactions={attendees}
													set={
														setInfo
															? {
																	id: setInfo.id,
																	artist: {
																		id: setInfo.artist.id,
																		name: setInfo.artist.name,
																		image_url: setInfo.artist.image_url,
																		spotify_genres: null,
																		manual_genres: null,
																		aliases: [],
																	},
																	stage: null,
																	start_time: setInfo.start_time || "",
																	end_time: setInfo.end_time || "",
																	day: "unknown",
																	startHour: 0,
																	durationHours: 1,
																	time: "",
																	is_surprise: undefined,
																	placeholder_name: null,
																	alias_name: setInfo.alias_name,
																	is_group_alias: true,
																	presents_name: null,
																	friends_set: undefined,
																	is_collaborative: true,
																	display_format: undefined,
																	special_guests: null,
																	set_artists: [],
																}
															: undefined
													}
													festivalSlug={festival.slug}
													maxAvatars={4}
													size="sm"
													disableLinks={true}
												/>
											</FacepileContainer>
										</div>
									</div>
								</div>
							</div>
						</div>
					</CardContent>
				</Link>
			</Card>
		);
	},
);

ArtistFestivalCard.displayName = "ArtistFestivalCard";
